import React, { useState, useEffect, useRef } from 'react';
import { postOverviewAiQuestion, generateAiSummary } from '../api';
import { formatMarkdown } from '../utils/markdownFormatter';
import { format } from 'date-fns';

interface StaticAiPanelProps {
  insight: string;
  contextType: 'overview' | 'iteration' | 'team' | 'developer' | 'project'
  | 'project-developer' | 'developer-iteration' | 'all-iterations' | 'support-overview';
  entityName?: string;
  entityId?: string;
  projectId?: string;
  createdOn?: string;
  canGenerate?: boolean;
  onStreamedContentChange?: (summary: string) => void;
}

const StaticAiPanel: React.FC<StaticAiPanelProps> = ({
  insight,
  contextType,
  entityName = '',
  entityId = '',
  projectId = '',
  createdOn = '',
  canGenerate = true,
  onStreamedContentChange
}) => {
  // Check if we're in mobile view
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [generating, setGenerating] = useState<boolean>(false);
  const [streamedContent, setStreamedContent] = useState<string>('');
  const [cachedSummaries, setCachedSummaries] = useState<Record<string, string>>({});
  const [currentStream, setCurrentStream] = useState<ReadableStreamDefaultReader | null>(null);
  const [lastEntityId, setLastEntityId] = useState<string>('');
  const [createdOnState, setCreatedOn] = useState<string>(createdOn);
  const [canGenerateState, setCanGenerate] = useState<boolean>(canGenerate);

  useEffect(() => {
    if (streamedContent) {
      setCachedSummaries(prev => ({ ...prev, [entityId]: streamedContent }));
    }
  }, [streamedContent, entityId]);

  useEffect(() => {
    if (streamedContent && onStreamedContentChange && streamedContent !== cachedSummaries[entityId]) {
      onStreamedContentChange(streamedContent);
    }
  }, [streamedContent, onStreamedContentChange, entityId, cachedSummaries]);

  useEffect(() => {
    if (currentStream && currentStream instanceof ReadableStreamDefaultReader) {
      currentStream.cancel();
      setCurrentStream(null);
      setStreamedContent('');
      handleGenerateSummary(false);
    }
  }, [entityId, entityName]);

  // Handle entity/project changes - clear content and regenerate
  useEffect(() => {
    // If entityId has changed, clear the current content and regenerate
    if (entityId && entityId !== lastEntityId) {
      // Cancel any ongoing stream
      if (currentStream) {
        currentStream.cancel();
        setCurrentStream(null);
      }
      
      // Clear current content to show the generate button
      setStreamedContent('');
      setLastEntityId(entityId);
      
      // If we have cached content for this entity, use it
      if (cachedSummaries[entityId]) {
        setStreamedContent(cachedSummaries[entityId]);
      }
    }
  }, [entityId, lastEntityId, currentStream, cachedSummaries]);

  useEffect(() => {
    if (createdOn) {
        // Check if it's already a localized date string by trying to parse it and compare
        const parsedDate = new Date(createdOn);
        
        // If parsedDate is valid and the string does NOT match the toLocaleDateString of that date
        if (!isNaN(parsedDate.getTime()) && createdOn !== parsedDate.toLocaleDateString()) {
          const d = format(parsedDate, 'do MMMM yyyy');
          setCreatedOn(d);
        } else {
          const d = format(createdOn, 'do MMMM yyyy');
          setCreatedOn(d);
        }
    }
  }, [createdOn]);

  useEffect(() => {
    setCanGenerate(canGenerate);
  }, [canGenerate]);

  // Add useEffect to load Material Icons font
  useEffect(() => {
    // Add Material Icons font if it doesn't exist
    if (!document.querySelector('link[href*="material-icons"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
      document.head.appendChild(link);
    }
  }, []);

  const handleGenerateSummary = async (skipCache = false) => {
    if (!entityId) return;

    // Interrupt any ongoing stream
    if (currentStream) {
      await currentStream.cancel();
      setCurrentStream(null);
    }

    try {
      setGenerating(true);
      setStreamedContent('');

      if (cachedSummaries[entityId] && !skipCache) {
        setStreamedContent(cachedSummaries[entityId]);
        setGenerating(false);
        return;
      }

      const stream = await generateAiSummary(contextType, entityId, projectId,undefined,true,canGenerateState);
      setCurrentStream(stream.stream);

      for await (const chunk of stream) {
        setStreamedContent(prev => prev + chunk);
      }
      setCreatedOn(format(new Date(), 'do MMMM yyyy'));
      setCanGenerate(false);
      setGenerating(false);
    } catch (err) {
      console.error('Error generating summary:', err);
      setStreamedContent('');
      setGenerating(false);
    } finally {
      setCurrentStream(null);
    }
  };



  return (
    <div className="static-ai-panel"
      style={{
        height: '100%',
        // color: '#333',
        display: 'flex',
        flexDirection: 'column',        
        borderRadius: isMobile ? 0 : 12,
        background: '#fff',
        border: isMobile ? 'none' : '3px solid #1a237e',
        boxShadow: isMobile ? 'none' : '6px 4px 12px rgba(0, 0, 0, 0.3)',
        position: 'relative',
        overflow: 'hidden',
        padding: isMobile ? '0.5rem' : '1rem',
        minHeight: 0,
        flex: 1
      }}
    >
      {/* Title and Regenerate Button */}
      {(insight || streamedContent) && (
        <div
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: isMobile ? 'center' : 'space-between',
            alignItems: isMobile ? 'center' : 'center',
            textAlign: isMobile ? 'center' : 'left',
            gap: isMobile ? '0.5rem' : '0',
            marginBottom: '0.5rem',
            paddingBottom: '1rem',
            borderBottom: '1px solid #1a237e' 
          }}
        >
          <h2 style={{ margin: 0 }}>
            Analysis {entityName && `- ${entityName}`}
          </h2>

          {/* Desktop right-side actions */}
          {!isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {createdOnState && (
                <div style={{ fontSize: '0.8rem', color: '#666', textAlign: 'right' }}>
                  Generated: {createdOnState}
                </div>
              )}
              {canGenerateState && (
                <button
                  onClick={() => handleGenerateSummary(true)}
                  disabled={generating}
                  title="Refresh Analysis"
                  style={{
                    background: generating ? 'rgba(57, 73, 171, 0.2)' : 'rgba(57, 73, 171, 0.15)',
                    color: '#3949ab',
                    border: '1px solid rgba(57, 73, 171, 0.3)',
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    cursor: generating ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    if (!generating) {
                      e.currentTarget.style.background = 'rgba(57, 73, 171, 0.25)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!generating) {
                      e.currentTarget.style.background = 'rgba(57, 73, 171, 0.15)';
                    }
                  }}
                >
                  {generating ? (
                    <>
                      <div
                        style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid rgba(57, 73, 171, 0.3)',
                          borderTop: '2px solid #3949ab',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}
                      />
                      Generating...
                    </>
                  ) : (
                    'Regenerate'
                  )}
                </button>
              )}
            </div>
          )}

          {/* Mobile: actions underneath title, centered */}
          {isMobile && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              {canGenerateState ? (
                <button
                  onClick={() => handleGenerateSummary(true)}
                  disabled={generating}
                  title="Refresh Analysis"
                  style={{
                    background: generating ? 'rgba(57, 73, 171, 0.2)' : 'rgba(57, 73, 171, 0.15)',
                    color: '#3949ab',
                    border: '1px solid rgba(57, 73, 171, 0.3)',
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    cursor: generating ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    if (!generating) {
                      e.currentTarget.style.background = 'rgba(57, 73, 171, 0.25)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!generating) {
                      e.currentTarget.style.background = 'rgba(57, 73, 171, 0.15)';
                    }
                  }}
                >
                  {generating ? (
                    <>
                      <div
                        style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid rgba(57, 73, 171, 0.3)',
                          borderTop: '2px solid #3949ab',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}
                      />
                      Generating...
                    </>
                  ) : (
                    'Regenerate'
                  )}
                </button>
              ) : (
                createdOnState && (
                  <div style={{ fontSize: '0.85rem', color: '#666', fontStyle: 'italic' }}>
                    Generated: {createdOnState}
                  </div>
                )
              )}
            </div>
          )}
        </div>
      )}

      {/* Generate Button or Insight Content */}
      {!insight && !streamedContent ? (
        <div style={{ textAlign: 'center', padding: '2rem', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <button
            onClick={() => handleGenerateSummary(true)}
            disabled={generating}
            style={{
              background: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              padding: '16px 24px',
              cursor: generating ? 'not-allowed' : 'pointer',
              opacity: generating ? 0.7 : 1,
              transition: 'all 0.2s ease',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              transform: 'translateY(0)'
            }}
            onMouseEnter={(e) => {
              if (!generating) {
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <span className="material-icons" style={{ fontSize: '48px', color: '#3949ab' }}>
              bar_chart
            </span>
            <div style={{ marginTop: '8px', fontSize: '0.9rem', color: '#666' }}>
              Generate analysis for {entityName}
            </div>
          </button>
        </div>
      ) : (
        <div
          className="markdown-content"
          style={{
            fontSize: 16,
            lineHeight: 1.6,
            flex: 1,
            overflowY: 'auto',
            color: '#333',
            padding: isMobile ? '8px 8px 0 8px' : '16px',
            marginBottom: isMobile ? '0.5rem' : '0',
            minHeight: 0
          }}
          dangerouslySetInnerHTML={{ __html: formatMarkdown(streamedContent || insight) }}
        />
      )}

      {/* Add custom styling for markdown content */}
      {/* Add custom styling for markdown content */}
      <style>{`
          .markdown-content h2 {
            color: #143e87;
            font-size: 1.3rem;
            margin-top: 1.5rem;
            margin-bottom: 0.75rem;
          }
          .markdown-content h3 {
            color: #143e87;
            font-size: 1.1rem;
            margin-top: 1.2rem;
            margin-bottom: 0.5rem;
          }
          .markdown-content p {
            margin-bottom: 1rem;
          }
          .markdown-content ul, .markdown-content ol {
            margin-left: 1.5rem;
            margin-bottom: 1rem;
          }
          .markdown-content li {
            margin-bottom: 0.5rem;
          }
          .markdown-content strong {
            color: #143e87;
            font-weight: 600;
          }
        `}</style>


    </div>
  );
};

export default StaticAiPanel;
